import {
  EMAIL_SIGNATURE_WIDTH,
  EMAIL_SIGNATURE_WIDTH_WITHOUT_IMAGE,
} from '@components/EmailSignaturev2/consts';
// components
import EmailSignaturePreviewBox, {
  EmailSignaturePreviewBoxLite,
} from '@components/EmailSignaturev2/EmailSignatureEdit/EmailSignaturePreviewBox';
import EmailSignatureCompactLayout from '@components/EmailSignaturev2/EmailSignatureEdit/layouts/compact/EmailSignatureCompactLayout';
import CompactPreviewShimmer from '@components/EmailSignaturev2/EmailSignatureEdit/layouts/compact/EmailSignatureCompactLayoutShimmer';
import type { EmailSignatureV2 } from '@components/EmailSignaturev2/types';
import { faLock } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useNode from '@hooks/use-node';
// hooks
import { useRect } from '@reach/rect';
import { type FC, useEffect, useMemo, useRef } from 'react';

import styles from './OnboardingPreviewContainer.module.scss';

interface OnboardingPreviewContainerProps {
  emailSignature: EmailSignatureV2;
  emailSignatureHasLoaded: boolean;
  isLiteVersion?: boolean;
  isWithoutImage?: boolean;
  isLocked?: boolean;
  lockedScreenText?: string;
}

const OnboardingPreviewContainer: FC<React.PropsWithChildren<OnboardingPreviewContainerProps>> = ({
  emailSignature,
  emailSignatureHasLoaded,
  isLiteVersion = false,
  isLocked = false,
  isWithoutImage = false,
  lockedScreenText,
}) => {
  const previewBodyRef = useRef<HTMLDivElement | null>(null);
  const previewBodyRect = useRect(previewBodyRef, {
    observe: true,
  });
  const previewBodyWrapperRef = useRef<HTMLDivElement | null>(null);
  const previewBodyWrapperRect = useRect(previewBodyWrapperRef, {
    observe: true,
  });
  const [emailSignatureNode, emailSignatureRef] = useNode<HTMLTableElement>();

  const scaleFactor = useMemo(() => {
    if (!previewBodyRect || !previewBodyRef.current) {
      return 1;
    }
    const previewBodyStyle = getComputedStyle(previewBodyRef.current);

    const padding = parseFloat(previewBodyStyle.paddingLeft) + parseFloat(previewBodyStyle.paddingRight);
    const fullWidthValue = isWithoutImage ? EMAIL_SIGNATURE_WIDTH_WITHOUT_IMAGE : EMAIL_SIGNATURE_WIDTH;

    return Math.min((previewBodyRect.width - padding) / fullWidthValue, 1);
  }, [previewBodyRect, isWithoutImage]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: historic
  useEffect(() => {
    if (!previewBodyWrapperRef.current || !emailSignatureNode) {
      return;
    }

    // we have to scale back the signature to display it nicely,
    // so we have to set the container's width and height to this scaled size to avoid having empty white spaces
    const scaledSignatureHeight = emailSignatureNode.offsetHeight * scaleFactor;
    const scaledSignatureWidth = emailSignatureNode.offsetWidth * scaleFactor;

    previewBodyWrapperRef.current.style.height = `${scaledSignatureHeight}px`;
    previewBodyWrapperRef.current.style.width = `${scaledSignatureWidth}px`;
  }, [emailSignatureNode, previewBodyWrapperRect, scaleFactor]);

  const PreviewBox = isLiteVersion ? EmailSignaturePreviewBoxLite : EmailSignaturePreviewBox;

  return (
    <div className={styles['preview-signature-container']}>
      <PreviewBox>
        {isLiteVersion === false && (
          <div className={styles['dummy-email-message']}>Your email message here</div>
        )}
        <div ref={previewBodyRef} className={styles['preview-signature-body']}>
          <div
            style={{ transform: `scale(${scaleFactor})` }}
            className={styles['preview-signature-body-container']}
            ref={previewBodyWrapperRef}
          >
            {!emailSignatureHasLoaded && (
              <div className={styles['shimmer-email-signature-container']} ref={emailSignatureRef}>
                <CompactPreviewShimmer />
              </div>
            )}

            {emailSignatureHasLoaded && (
              <EmailSignatureCompactLayout
                ref={emailSignatureRef}
                emailSignature={emailSignature}
                disableAllSpecialEffects={true}
                disableAnchorLink={true}
              />
            )}
          </div>

          {isLocked && (
            <div className={styles['locked-veneer-layer']}>
              <FontAwesomeIcon size="2x" icon={faLock} />
              <p>{lockedScreenText}</p>
            </div>
          )}
        </div>
      </PreviewBox>
    </div>
  );
};

export default OnboardingPreviewContainer;
